import React, { useMemo, useState } from 'react';
import { Card, Alert, Button, Segmented, List, Avatar, Tag, Modal, Tabs, Input, Upload, Space, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FileOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

const AddinsConfigration = () => {
  const [allowUnverified, setAllowUnverified] = useState(true);
  const [addins, setAddins] = useState([
    { key: 'collision', name: 'Collision Reconstruction', status: 'verified' },
    { key: 'data-connector', name: 'Data Connector Demo', status: 'verified' },
    { key: 'drive-fuel-tracker', name: 'Drive Fuel Tracker (by Geotab)', status: 'verified' },
    { key: 'eld-info', name: 'ELD Info', status: 'verified' },
    { key: 'eld-settings', name: 'EldSettingsValidator', status: 'verified' },
    { key: 'evsa', name: 'EVSA', status: 'verified' },
    { key: 'iox-output', name: 'IOX Output', status: 'unverified' },
    { key: 'last-insert-key-report', name: 'Last Insert Key Report', status: 'unverified' },
    { key: 'map-replay', name: 'MapReplay', status: 'unverified' },
    { key: 'camera-addin', name: 'mygeotab-camera-addin', status: 'verified' },
    { key: 'roadside', name: 'Roadside Assistance', status: 'verified' },
    { key: 'tabs', name: 'Tabs', status: 'unverified' },
  ]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // { key, name }
  const [activeModalTab, setActiveModalTab] = useState('config');
  const [configJson, setConfigJson] = useState(
    '{\n  "name": "",\n  "supportEmail": "ramisj12@gmail.com",\n  "version": "1.0",\n  "items": [\n    {\n      "url": "",\n      "path": "",\n      "menuName": {\n        "en": ""\n      }\n    }\n  ]\n}'
  );

  // Map of known add-ins to their manifest URLs (based on provided screenshots)
  const manifestByKey = useMemo(() => ({
    collision: 'https://app.geotab.com/addins/geotab/accidentreconstruction/manifest.json',
    'data-connector': 'https://app.geotab.com/addins/geotab/geotab-data-connector-demo/manifest.json',
    'drive-fuel-tracker': 'https://app.geotab.com/addins/geotab/drive-fuel-tracker/manifest.json',
    'eld-info': 'https://app.geotab.com/addins/geotab/eld-support/manifest.json',
    'eld-settings': 'https://app.geotab.com/addins/geotab/eld-settings-validator/manifest.json',
    evsa: 'https://app.geotab.com/addins/geotab/evsa/manifest.json',
    'map-replay': 'https://app.geotab.com/addins/mygeotabcameraaddin/production/manifest.json',
    'camera-addin': 'https://app.geotab.com/addins/mygeotabcameraaddin/production/manifest.json',
    roadside: 'https://app.geotab.com/addins/geotab/roadside-assistance/manifest.json',
  }), []);

  // Full preset configurations for specific add-ins
  const presetConfigByKey = useMemo(() => ({
    tabs: `{
  "name": "Tabs",
  "supportEmail": "justin@fleet-nomics.com",
  "version": "1.0",
  "items": [
    {
      "url": "signInButton.html",
      "path": "SurveillanceCameras",
      "menuName": {
        "en": "Surveillance Cameras"
      },
      "svgIcon": "https://geotab-images.s3.us-east-2.amazonaws.com/icon.svg"
    }
  ],
  "isSigned": false
}`,
    'iox-output': `{
  "name": "IOX Output",
  "supportEmail": "support@geotab.com",
  "version": "1.1.0",
  "items": [
    {
      "url": "https://cdn.jsdelivr.net/gh/Geotab/sdk-addin-samples@master/addin-iox-output/dist/ioxOutput.html",
      "path": "AdministrationLink/",
      "menuName": {
        "en": "IOX Output"
      },
      "icon": "https://cdn.jsdelivr.net/gh/Geotab/sdk-addin-samples@master/addin-iox-output/dist/images/icon.svg"
    }
  ],
  "isSigned": false
}`,
    'last-insert-key-report': `{
  "name": "Last Insert Key Report",
  "supportEmail": "dmitryradyno@geotab.com",
  "version": "1.0",
  "items": [
    {
      "url": "https://www.geotab.com/mygeotab/addin/lastkeyinsert/lastinsertkey.html",
      "path": "ActivityLink/",
      "menuName": {
        "en": "Last Insert Key Report"
      }
    }
  ],
  "isSigned": false
}`,
  }), []);

  const openAddModal = () => {
    setEditing(null);
    setActiveModalTab('config');
    setConfigJson(
      '{\n  "name": "",\n  "supportEmail": "ramisj12@gmail.com",\n  "version": "1.0",\n  "items": [\n    {\n      "url": "",\n      "path": "",\n      "menuName": {\n        "en": ""\n      }\n    }\n  ]\n}'
    );
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditing(item);
    setActiveModalTab('config');
    const preset = presetConfigByKey[item?.key];
    if (preset) {
      setConfigJson(preset);
    } else {
      const url = manifestByKey[item?.key] || '';
      setConfigJson(url ? `{
  "url": "${url}"
}` : `{
  "url": ""
}`);
    }
    setModalOpen(true);
  };

  const handleDelete = (key) => {
    setAddins(prev => prev.filter(a => a.key !== key));
  };

  const statusTag = (status) => (
    <Tag color={status === 'verified' ? 'green' : 'orange'} style={{ marginInlineStart: 8 }}>
      {status === 'verified' ? 'Verified' : 'Unverified'}
    </Tag>
  );

  const dataToRender = useMemo(() => addins, [addins]);

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Add-Ins</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>Add-In</Button>
      </div>

      <Card bodyStyle={{ paddingTop: 12 }}>
        <Alert
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          message="Unverified Add-Ins could have potential security risks. We recommend performing a security audit before allowing them."
          style={{ marginBottom: 12 }}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div style={{ minWidth: 180 }}>Allow unverified Add-Ins</div>
          <Segmented
            options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]}
            value={allowUnverified}
            onChange={(v) => setAllowUnverified(Boolean(v))}
          />
        </div>

        <List
          itemLayout="horizontal"
          dataSource={dataToRender}
          renderItem={(item) => (
            <List.Item
              actions={[
                statusTag(item.status),
                <Tooltip key="edit" title="Edit">
                  <Button type="text" icon={<EditOutlined />} onClick={() => openEditModal(item)} />
                </Tooltip>,
                <Tooltip key="delete" title="Delete">
                  <Button danger type="text" icon={<DeleteOutlined />} onClick={() => handleDelete(item.key)} />
                </Tooltip>
              ]}
              style={{ paddingLeft: 8, paddingRight: 8 }}
            >
              <List.Item.Meta
                avatar={<Avatar size={24} icon={<FileOutlined />} />}
                title={
                  <Space>
                    <span style={{ fontWeight: 500 }}>{item.name}</span>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Card>

      <Modal
        title={editing ? `Add-In - ${editing.name}` : 'Add-In'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Tabs
          activeKey={activeModalTab}
          onChange={setActiveModalTab}
          items={[
            {
              key: 'config',
              label: 'Configuration',
              children: (
                <div>
                  <div style={{ marginBottom: 8 }}>Configuration</div>
                  <Input.TextArea
                    rows={14}
                    value={configJson}
                    onChange={(e) => setConfigJson(e.target.value)}
                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}
                  />
                  <div style={{ marginTop: 12, textAlign: 'right' }}>
                    <Space>
                      <Button onClick={() => setModalOpen(false)}>Cancel</Button>
                      <Button type="primary" onClick={() => setModalOpen(false)}>Done</Button>
                    </Space>
                  </div>
                </div>
              )
            },
            {
              key: 'files',
              label: 'Files',
              children: (
                <div>
                  <Upload.Dragger name="file" multiple={false} showUploadList>
                    <p className="ant-upload-drag-icon">
                      <FileOutlined />
                    </p>
                    <p className="ant-upload-text">Drop file here or click to browse</p>
                    <p className="ant-upload-hint">Drag your ZIP-archived Add-In or Add-In configuration file.</p>
                  </Upload.Dragger>
                  <div style={{ marginTop: 12, textAlign: 'right' }}>
                    <Space>
                      <Button onClick={() => setModalOpen(false)}>Cancel</Button>
                      <Button type="primary" onClick={() => setModalOpen(false)}>Done</Button>
                    </Space>
                  </div>
                </div>
              )
            }
          ]}
        />
      </Modal>
    </div>
  );
};

export default AddinsConfigration;
